import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useTurnstile } from '../hooks/useTurnstile';

export function SignIn() {
  const { user, loading, accessInfo, accessLoading, signInWithGoogle, sendEmailLink, signOut, configError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(() => {
    return localStorage.getItem('tos-accepted') === 'true';
  });
  const { token: turnstileToken, isReady: turnstileReady, containerRef: turnstileRef, reset: resetTurnstile } = useTurnstile();

  // Redirect if already signed in with access
  useEffect(() => {
    if (!loading && !accessLoading && user && accessInfo?.hasAccess) {
      navigate('/landing');
    }
  }, [loading, accessLoading, user, accessInfo, navigate]);

  // Show configuration error
  if (configError) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-red-500/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-2">Configuration Error</h1>
              <p className="text-gray-400 text-sm">{configError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleGoogleSignIn = async () => {
    if (!privacyConsent) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }
    setError(null);
    setIsLoading(true);
    const { error } = await signInWithGoogle();
    setIsLoading(false);
    if (error) {
      setError(error.message);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    if (!privacyConsent) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    setError(null);
    setIsLoading(true);
    const { success, error } = await sendEmailLink(email);
    setIsLoading(false);

    if (success) {
      setEmailSent(true);
    } else if (error) {
      setError(error.message);
      resetTurnstile();
    }
  };

  // Show access denied state
  if (user && accessInfo && !accessInfo.hasAccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-[#2a2a2a]">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-2">Access Pending</h1>
              <p className="text-gray-400">
                {accessInfo.accessLevel === 'waitlist'
                  ? "You're on the waitlist! We'll notify you when access is granted."
                  : "Your account doesn't have access to the demo yet."}
              </p>
            </div>

            <div className="bg-[#0a0a0a] rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-400">Signed in as</p>
              <p className="text-white font-medium">{user.email}</p>
            </div>

            {accessInfo.accessLevel === 'none' && (
              <a
                href="https://pistachiorama.ai"
                className="block w-full py-3 px-4 bg-[#93c572] hover:bg-[#7eb356] text-black font-medium rounded-lg text-center transition-colors mb-4"
              >
                Join the Waitlist
              </a>
            )}

            <button
              onClick={async () => {
                await signOut();
                window.location.reload();
              }}
              className="w-full py-3 px-4 border border-[#333] text-gray-300 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              Sign in with a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading || accessLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#93c572] animate-spin" />
      </div>
    );
  }

  // Email sent confirmation
  if (emailSent) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-[#2a2a2a] text-center">
            <div className="w-16 h-16 bg-[#93c572]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#93c572]" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">Check your email</h1>
            <p className="text-gray-400 mb-6">
              We sent a sign-in link to <span className="text-white">{email}</span>
            </p>
            <button
              onClick={() => setEmailSent(false)}
              className="text-[#93c572] hover:underline"
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo width={80} height={80} />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight mb-2">Pistachiorama</h1>
          <p className="text-[#a1a1aa]">Sign in to access the demo</p>
        </div>

        <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-[#2a2a2a]">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#333]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#1a1a1a] text-gray-500">or</span>
            </div>
          </div>

          {/* Email Sign In */}
          <form onSubmit={handleEmailSignIn}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0a] border border-[#333] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#93c572] transition-colors"
                  required
                />
              </div>
            </div>

            {/* Turnstile Widget */}
            <div ref={turnstileRef} className="flex justify-center mb-4" />

            <button
              type="submit"
              disabled={isLoading || !email || !turnstileToken || !privacyConsent}
              className="w-full py-3 px-4 bg-[#93c572] hover:bg-[#7eb356] text-black font-medium rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : !turnstileReady ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Send sign-in link'
              )}
            </button>
          </form>

          {/* Terms of Service checkbox */}
          <label className="mt-6 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={privacyConsent}
              onChange={(e) => {
                setPrivacyConsent(e.target.checked);
                if (e.target.checked) {
                  localStorage.setItem('tos-accepted', 'true');
                } else {
                  localStorage.removeItem('tos-accepted');
                }
              }}
              className="mt-1 w-4 h-4 rounded border-[#333] bg-[#0a0a0a] text-[#93c572] focus:ring-[#93c572] focus:ring-offset-0"
            />
            <span className="text-sm text-gray-400">
              I agree to the{' '}
              <Link to="/terms" className="text-[#93c572] hover:underline">
                Terms of Service
              </Link>
            </span>
          </label>

          <p className="mt-4 text-center text-sm text-gray-500">
            No password needed. We'll email you a secure link.
            <br />
            <Link to="/privacy" className="text-[#93c572] hover:underline">
              Privacy Policy
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          Don't have access?{' '}
          <a href="https://pistachiorama.ai" className="text-[#93c572] hover:underline">
            Join the waitlist
          </a>
        </p>
      </div>
    </div>
  );
}
